import axios from "utils/axios";
import { useState, useCallback } from "react";

/**
 * Fetch template parameters from backend
 * @returns {Promise<Object>} Response with template parameters
 */
export const getTemplateParameters = async () => {
  try {
    const response = await axios.get('/api/Template/GetTemplateParameterList');
    
    if (response.data?.status === "Success" && response.data?.data) {
      // The data field is a JSON string, so we need to parse it first
      let categoriesData;
      if (typeof response.data.data === 'string') {
        categoriesData = JSON.parse(response.data.data);
      } else {
        categoriesData = response.data.data;
      }
      
      // Ensure categoriesData is an array
      if (!Array.isArray(categoriesData)) {
        throw new Error('Invalid data format: expected array of categories');
      }
      
      // Transform the data to match the expected format for template variables
      const templateVariables = {};
      const allParameters = [];
      
      // Iterate through categories and extract parameters
      categoriesData.forEach(category => {
        if (category.Parameters && Array.isArray(category.Parameters)) {
          category.Parameters.forEach(param => {
            // Skip if ParameterKey is missing or invalid
            if (!param.ParameterKey || typeof param.ParameterKey !== 'string') {
              return;
            }
            
            // Convert ParameterKey "{{FullName}}" to key "FullName"
            const key = param.ParameterKey.replace(/[{}]/g, '');
            templateVariables[key] = param.DefaultValue || '';
            
            // Store parameter with category info
            allParameters.push({
              ...param,
              CategoryName: category.CategoryName,
              CategoryId: category.CategoryId
            });
          });
        }
      });
      
      return {
        success: true,
        data: {
          variables: templateVariables,
          parameters: allParameters,
          categories: categoriesData
        },
        message: response.data.message
      };
    } else {
      return {
        success: false,
        error: response.data?.message || 'Failed to fetch template parameters',
        data: null
      };
    }
  } catch (error) {
    console.error('Error fetching template parameters:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch template parameters',
      data: null
    };
  }
};

/**
 * Get fallback template variables when API fails
 * @returns {Object} Default template variables
 */
export const getFallbackTemplateVariables = () => {
  return {
    CurrentYear: "2025",
    CurrentDate: "2025-09-03",
    CurrentTime: "15:16:43",
    FullName: "John Doe",
    Email: "example@gmail.com",
    PhoneNumber: "+917896541230",
    EmailOtp: "788546",
    EmailOtpExpiry: "2025-09-04 06:41:10.337",
    PhoneOtp: "784512",
    PhoneOtpExpiry: "2025-09-04 06:41:10.337"
  };
};

/**
 * Custom hook to fetch and manage template parameters
 * @returns {Object} Hook with parameters, loading state, and functions
 */
export const useTemplateParameters = () => {
  const [templateParameters, setTemplateParameters] = useState([]);
  const [templateVariables, setTemplateVariables] = useState({});
  const [templateCategories, setTemplateCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const fetchParameters = useCallback(async () => {
    if (isLoading) return; // Prevent multiple simultaneous calls
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getTemplateParameters();
      
      if (result.success) {
        setTemplateParameters(result.data.parameters);
        setTemplateVariables(result.data.variables);
        setTemplateCategories(result.data.categories || []);
      } else {
        setError(result.error);
        // Use fallback data
        const fallbackVariables = getFallbackTemplateVariables();
        setTemplateVariables(fallbackVariables);
        
        // Convert fallback to parameters format
        const fallbackParameters = Object.entries(fallbackVariables).map(([key, value]) => ({
          ParameterName: key,
          ParameterKey: `{{${key}}}`,
          DefaultValue: value,
          CategoryName: 'Default'
        }));
        setTemplateParameters(fallbackParameters);
        setTemplateCategories([]);
      }
      setHasInitialized(true);
    } catch (err) {
      setError(err.message);
      // Use fallback data
      const fallbackVariables = getFallbackTemplateVariables();
      setTemplateVariables(fallbackVariables);
      
      const fallbackParameters = Object.entries(fallbackVariables).map(([key, value]) => ({
        ParameterName: key,
        ParameterKey: `{{${key}}}`,
        DefaultValue: value,
        CategoryName: 'Default'
      }));
      setTemplateParameters(fallbackParameters);
      setTemplateCategories([]);
      setHasInitialized(true);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);

  return {
    templateParameters,
    templateVariables,
    templateCategories,
    isLoading,
    error,
    hasInitialized,
    fetchParameters,
    setTemplateVariables
  };
};
